import React, { useState, useCallback, useEffect } from 'react';
import { pick } from 'ramda';
import Form from 'js/components/common/Form';
import { useTranslations } from 'js/hooks/i18n';
import { Loader } from 'js/components/common/Loader';
import { Button } from 'js/components/common/Button';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { ReCaptcha } from 'js/components/main/ReCaptcha';
import { Input } from 'js/components/common/Fields/Input';
import { useUserSelector } from 'js/store/hooks';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { required, length } from 'js/utils/Validation/validationRules';

import * as hooks from './hooks';
import { TwoFactorWarningMessage } from '../TwoFactorWarningMessage';

import style from './TwoFactorAuthentication.scss';

const validationConfig = {
    token: [
        required({ error: 'playtech.mobile.system.2FA.popup.token.tooltip.required' }),
        length({ min: 6, max: 6, error: 'playtech.mobile.system.2FA.popup.token.tooltip.length' })
    ]
};

const useGetTitle = () => {
    const t = useTranslations('playtech.mobile.system.2FA.popup.title.');

    return useCallback((templateId) => {
        switch (templateId) {
            case 'two_factor_email': return t('email');
            case 'two_factor_sms': return t('SMS');
            default: return t('default');
        }
    }, [t]);
};

export const TwoFactorAuthentication = ({
    title,
    onClose,
    onCancel,
    onSuccess,
    templateId,
    resendHandler,
    showTrustDeviceOption
}) => {
    const [error, setError] = useState();
    const [isResendDisabled, setIsResendDisabled] = useState();
    const [isResendWarningShown, setIsResendWarningShown] = useState();
    const [displayTitle, setTitle] = useState();
    const isMounted = useIsMounted();
    const [isRecaptchaVisible, setIsRecaptchaVisible] = useState(false);

    const translate = useTranslations('playtech.mobile.system.2FA.popup.');
    const { isLoggedIn, rememberDevice } = useUserSelector(pick(['isLoggedIn', 'rememberDevice']));
    const isDeviceTrusted = rememberDevice && showTrustDeviceOption ? 1 : 0;

    const getTitle = useGetTitle();
    const getTitleAsync = hooks.useGetTitleAsync();
    const validateTwoFactorToken = hooks.useValidateTwoFactorToken({ onSuccess, onClose, setError, isDeviceTrusted });
    const resendCode = hooks.useResendCode({
        setError,
        isMounted,
        resendHandler,
        isResendDisabled,
        isRecaptchaVisible,
        setIsResendDisabled,
        setIsRecaptchaVisible,
        setIsResendWarningShown
    });
    const recaptchaErrorHandler = hooks.useRecaptchaErrorHandler({ setError, setIsRecaptchaVisible });

    useEffect(() => {
        if (displayTitle || !isMounted()) {
            return;
        }

        if (title) {
            setTitle(title);
        } else if (templateId) {
            setTitle(getTitle(templateId));
        } else if (isLoggedIn && isMounted()) {
            (async () => {
                const ttl = await getTitleAsync();

                isMounted() && setTitle(ttl);
            })();
        } else {
            setTitle(translate('title.default'));
        }
    }, [title, displayTitle, isLoggedIn, translate, setTitle, getTitle, isMounted, getTitleAsync, templateId]);

    useEffect(() => {
        isRecaptchaVisible
            ? setError(translate('playtech.mobile.system.recaptcha.error.captchaNotPassed'))
            : setError();
    }, [isRecaptchaVisible, setError, translate]);

    return <Form
        onSubmit={validateTwoFactorToken}
        validationConfig={validationConfig}
        render={({ submitting, handleSubmit }) => (
            <div>
                <form onSubmit={event => event.preventDefault()}>
                    {isResendWarningShown && (
                        <TwoFactorWarningMessage
                            setIsResendDisabled={setIsResendDisabled}
                            setIsResendWarningShown={setIsResendWarningShown}
                        />
                    )}
                    {error && (
                        <ErrorMessage
                            error={error}
                            animate={true}
                            className={style.error}
                        />
                    )}
                    {displayTitle
                        ? (<span className={style.description}>{displayTitle}</span>)
                        : (<Loader />)
                    }

                    <Input
                        type="text"
                        name="token"
                        className={style.control}
                        autoComplete="one-time-code"
                        placeholder={translate('token.placeholder')}
                        aria-label={translate(['token.placeholder', 'playtech.mobile.system.input.aria-label'])}
                    />
                    {isRecaptchaVisible && (
                        <div className={style.recaptcha}>
                            <ReCaptcha
                                operation="login"
                                onError={recaptchaErrorHandler}
                                onValidate={() => setIsRecaptchaVisible(false)}
                            />
                        </div>
                    )}
                    <span className={style.resendLink}>
                        {translate('didNotReceive')} <a href="#" className={`${isResendDisabled || isRecaptchaVisible ? style.isResendDisabled : ''}`} onClick={resendCode}>{translate('resend')}</a>
                    </span>
                    <div className={style.formActions}>
                        <Button
                            type="button"
                            color="negative"
                            onClick={onCancel}
                            className={style.btn}
                        >
                            {translate('playtech.mobile.system.cancel.button.title')}
                        </Button>
                        <Button
                            type="button"
                            className={style.btn}
                            onClick={handleSubmit}
                            disabled={submitting || isRecaptchaVisible}
                        >
                            {translate('button.verify')}
                        </Button>
                    </div>
                </form>
            </div>
        )}
    />;
};
